import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  QrCode, Sparkles, ClipboardCheck, MessageSquare, ArrowLeft, ArrowRight,
  Share2, Copy, Check, Stethoscope, Users, Zap,
} from 'lucide-react';

const SHARE_URL = 'https://docsite.care/whats-new/clinic-checkin-qr';
const SHARE_MESSAGE = (
  `New on DocSite — turn every clinic visit into a verified patient contact + instant feedback.\n\n` +
  `Print one QR. Patient sitting in front of you scans, enters name + mobile (15 sec). Their row pops up live on your Patient Log. They get an SMS with a 3-question feedback link to fill before walking out — and a 4th outcome question 7 days later.\n\n` +
  `20 patients/day = 500 consented contacts a month, all 100% legitimate.\n\n` +
  `Read more: ${SHARE_URL}`
);

export default function ClinicCheckinQrLaunch() {
  const [copied, setCopied] = useState(false);
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const whatsappHref = `https://wa.me/?text=${encodeURIComponent(SHARE_MESSAGE)}`;
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: 'DocSite — Clinic Check-In QR', text: SHARE_MESSAGE, url: SHARE_URL }); }
      catch { /* cancelled */ }
    } else { copyLink(); }
  };
  const copyLink = async () => {
    try { await navigator.clipboard.writeText(SHARE_URL); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch { /* clipboard denied */ }
  };

  return (
    <div className="min-h-screen bg-slate-100 print:bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      <Helmet>
        <title>Clinic Check-In QR — instant patient capture + auto-feedback | DocSite</title>
        <meta name="description" content="Print one QR. Every patient who scans becomes a verified consented contact + leaves a 3-question feedback before walking out, plus a Day-7 outcome rating. 20/day = 500 leads/month, zero spam risk." />
        <meta property="og:title" content="DocSite — Clinic Check-In QR" />
        <meta property="og:description" content="Every clinic visit → verified contact + instant feedback. Print one QR. 15-second scan." />
        <meta property="og:url" content={SHARE_URL} />
      </Helmet>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <Link to="/whats-new" className="inline-flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-800 mb-6"><ArrowLeft size={12} /> All updates</Link>

        <div className="rounded-3xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border border-emerald-200 p-6 sm:p-10 mb-6">
          <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2.5 py-1 rounded-full bg-emerald-600 text-white"><Sparkles size={10} /> Just Launched · 15 Feb 2026</span>
          <h1 className="text-2xl sm:text-4xl font-semibold text-slate-900 mt-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Every clinic visit → a verified patient contact + a 3-question feedback before they walk out.
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-3 leading-relaxed">
            Print <b>one QR</b>. Place it on your consult-desk. Every patient who scans becomes a legitimate, opted-in contact in your Patient Log within 10 seconds — and gets an instant SMS with a quick feedback link to fill <b>before walking out of your clinic</b>. A 4th outcome question fires automatically 7 days later.
          </p>
          <div className="mt-5 grid grid-cols-3 gap-3">
            <div className="rounded-xl bg-white border border-slate-200 px-3 py-3 text-center">
              <p className="text-xl font-semibold text-emerald-600">15s</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-0.5">Patient scan → submit</p>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 px-3 py-3 text-center">
              <p className="text-xl font-semibold text-emerald-600">500</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-0.5">Consented contacts / month</p>
            </div>
            <div className="rounded-xl bg-white border border-slate-200 px-3 py-3 text-center">
              <p className="text-xl font-semibold text-emerald-600">3 + 1</p>
              <p className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-0.5">Feedback Qs · same-day + Day-7</p>
            </div>
          </div>
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-5 space-y-5">
          <h2 className="text-lg font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>How it works</h2>
          {[
            { n: 1, icon: QrCode, title: 'Print the QR once', body: 'Generated in your dashboard → Clinic Kit. Place it on the consult-desk, hand it to the patient on first visit, or stick it next to the prescription pad.' },
            { n: 2, icon: Stethoscope, title: 'Patient scans + types name + mobile', body: 'Mobile-first form. No login. No OTP — they\'re physically in front of you. Optional "what\'s bothering you?" field they can skip. Submits in 15 seconds.' },
            { n: 3, icon: ClipboardCheck, title: 'Row appears live on your Patient Log', body: 'A green "✨ new check-in" card shows the patient at the top of your Patient Log within 10 seconds of their scan. Add channel + final symptom + tap Save when you finish the consult.' },
            { n: 4, icon: MessageSquare, title: '3-question feedback, before they walk out', body: 'Patient gets an SMS in seconds with a 30-second, 3-question feedback link. Most fill it sitting in the parking lot. 4th outcome question fires automatically Day 7.' },
            { n: 5, icon: Users, title: 'Builds your legit broadcast cohort', body: 'Every check-in row is a consented patient (they physically scanned in your clinic). Eligible for the upcoming Existing-Patient Re-Engagement Blast — clean, no WhatsApp ban risk, no spam.' },
          ].map(s => (
            <div key={s.n} className="flex items-start gap-3.5">
              <div className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <s.icon size={16} className="text-emerald-700" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900"><span className="text-emerald-700 mr-2">{s.n}.</span>{s.title}</p>
                <p className="text-xs text-slate-600 mt-1 leading-relaxed">{s.body}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="rounded-2xl bg-white border border-slate-200 p-6 mb-5">
          <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2"><Zap size={16} className="text-amber-500" /> The math</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            Seeing 20 patients a day? · 25 working days = <b>500 verified contacts in your first month</b>. After 3 months that's 1,500. A genuine connect with your patients is established and they can reach you so much more easily. You can reach them through the <b>DocSite MyPatient Messaging Platform</b> to update them on your latest research findings — for better health of all.
          </p>
        </div>

        <div className="rounded-2xl bg-gradient-to-r from-sky-600 to-emerald-600 text-white p-6 text-center">
          <p className="text-base font-semibold mb-1">Set it up in 60 seconds</p>
          <p className="text-xs text-sky-50 mb-4">Open Dashboard → Clinic Kit → Print the Clinic Check-In QR. Tape it where the patient sits.</p>
          <Link to="/dashboard" className="inline-block bg-white text-sky-700 hover:bg-sky-50 rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="cta-go-to-dashboard">
            Go to my dashboard <ArrowRight size={14} className="inline ml-1" />
          </Link>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 justify-center print:hidden">
          <a href={whatsappHref} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-emerald-500 text-white hover:bg-emerald-600">
            <Share2 size={12} /> Share on WhatsApp
          </a>
          <button onClick={nativeShare} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800">
            <Share2 size={12} /> Share
          </button>
          <button onClick={copyLink} className="inline-flex items-center gap-1.5 text-xs px-3 py-2 rounded-full border border-slate-300 text-slate-700 hover:bg-slate-50">
            {copied ? <Check size={12} /> : <Copy size={12} />} {copied ? 'Copied' : 'Copy link'}
          </button>
        </div>
      </div>
    </div>
  );
}
